import { Box, Flex, HStack } from "@chakra-ui/layout";
import { Skeleton } from "@chakra-ui/skeleton";
import {
  CreatableSelect,
  GroupBase,
  MultiValue,
  OptionBase,
  Props,
  Select,
  SingleValue,
} from "chakra-react-select";
import React, { useMemo } from "react";
import { BaseFieldProps, FieldWrapper } from "../FieldWrapper/FieldWrapper";
import { cloneDeep } from "lodash";
import { BoxProps } from "@chakra-ui/react";
import { FieldInputProps } from "formik";

interface SelectFieldProps extends BaseFieldProps {
  boxProps?: BoxProps;
  options: { label: string; value: string | null }[];
  showOnTop?: boolean;
  allowEmpty?: boolean | string;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
  keepSelectionOrderInOptions?: boolean;
  creatable?: boolean;
}

export interface SelectOption extends OptionBase {
  value: string | null;
  label: string;
}
type OptionList = Array<SelectOption>;

export const chakraStyles = {
  control: (provided: any) => ({
    ...provided,
    fontSize: "xs",
    minH: 8,
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    py: 0,
  }),
  input: (provided: any) => ({
    ...provided,
  }),
  option: (provided: any) => ({
    ...provided,
    fontSize: "xs",
  }),
  multiValueLabel: (provided: any) => ({
    ...provided,
    fontSize: "xs",
  }),
};

export const ChakraReactSelectField = ({
  label,
  name,
  id,
  options,
  allowEmpty,
  showOnTop,
  helperText,
  isLoading,
  isMulti,
  fcProps,
  labelProps,
  valueProps,
  boxProps,
  horizontal,
  leftElement,
  rightElement,
  hideField,
  keepSelectionOrderInOptions,
  creatable,
  onChange: onChangeProp,
  ...inputProps
}: SelectFieldProps & Props<SelectOption, any, GroupBase<SelectOption>>) => {
  const SelectComponent = creatable ? CreatableSelect : Select;
  const mergedOptions = (currentValue: any) => {
    if (isMulti == null || !isMulti) return mergeSingleOptions(currentValue);
    return mergeMultipleOptions(currentValue);
  };

  const mergeSingleOptions = (currentValue: string) => {
    return useMemo(() => {
      const combinedOptions: OptionList = [];
      if (allowEmpty)
        combinedOptions.push({
          value: null,
          label: typeof allowEmpty == "string" ? allowEmpty : "-",
        });
      if (
        !!currentValue &&
        !options.find(({ value }) => value == currentValue)
      ) {
        return combinedOptions.concat(options).concat([
          {
            value: currentValue!,
            label: `${currentValue!} (deprecated)`,
          },
        ]);
      } else {
        return combinedOptions.concat(options);
      }
    }, [options]);
  };

  const mergeMultipleOptions = (currentValues: string[] | null) => {
    return useMemo(() => {
      if (
        currentValues === undefined ||
        currentValues === null ||
        !currentValues.length
      )
        return options;
      let result = cloneDeep(options);
      currentValues.forEach((currValue) => {
        if (!!currValue && !options.find(({ value }) => value === currValue)) {
          result = result.concat([
            {
              value: currValue!,
              label: `${currValue!} (deprecated)`,
            },
          ]);
        }
      });
      return result;
    }, [options]);
  };

  const selectValue = (field: FieldInputProps<any>) => {
    if (field.value && isMulti) {
      if (keepSelectionOrderInOptions) {
        return field.value.map((value: string) => ({
          value,
          label: value,
        }));
      }

      return mergedOptions(field.value).filter(
        ({ value }) =>
          value ==
          (field.value as string[]).find((currValue) => currValue == value)
      );
    } else {
      return mergedOptions(field.value).find(
        ({ value }) => value == field.value
      );
    }
  };

  return (
    <>
      {(isLoading && (
        <Flex
          w="100%"
          flexDir={horizontal ? "row" : "column"}
          alignItems={horizontal ? "center" : undefined}
          gridGap={2}
        >
          <Skeleton
            w="100%"
            height="18px"
            flex={horizontal ? labelProps?.flex ?? 1 : undefined}
          />
          <Skeleton
            w="100%"
            height={8}
            flex={horizontal ? valueProps?.flex ?? 3 : undefined}
          />
        </Flex>
      )) || (
        <FieldWrapper
          name={name}
          id={id}
          label={label}
          helperText={helperText}
          horizontal={horizontal}
          labelProps={labelProps}
          valueProps={valueProps}
          hideField={hideField}
          fcProps={fcProps}
        >
          {({ field, form }) => (
            <>
              <HStack w="100%">
                {leftElement}
                <Box flex={1} bgColor="white" {...boxProps}>
                  <SelectComponent<SelectOption, any, GroupBase<SelectOption>>
                    chakraStyles={chakraStyles}
                    {...inputProps}
                    value={selectValue(field)}
                    {...(showOnTop && {
                      styles: {
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      },
                    })}
                    {...(showOnTop && {
                      menuPortalTarget: document.querySelector("body"),
                    })}
                    isMulti={isMulti}
                    placeholder={helperText}
                    options={mergedOptions(field.value)}
                    onChange={(newValue, actionMeta) => {
                      const nextValue = isMulti
                        ? (newValue as MultiValue<SelectOption>).map(
                            (value) => value.value
                          )
                        : (newValue as SingleValue<SelectOption>)?.value;
                      form.setFieldValue(field.name, nextValue);
                      onChangeProp?.(newValue, actionMeta);
                    }}
                  />
                </Box>
                {rightElement}
              </HStack>
            </>
          )}
        </FieldWrapper>
      )}
    </>
  );
};
